import { useState } from 'react'
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  FolderTree,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react'
import { useTestSuites, useDeleteSuite } from '../api/useTestSuites'
import { useProjects } from '@/features/projects/api/useProjects'
import { SuiteDialog } from '../components/SuiteDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import type { TestSuite } from '@/types/suite.types'

const PAGE_SIZE = 25

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export default function TestSuitesPage() {
  // Filtering & pagination state
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [selectedSuite, setSelectedSuite] = useState<TestSuite | undefined>()

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [suiteToDelete, setSuiteToDelete] = useState<TestSuite | null>(null)

  // API hooks
  const { data: projects } = useProjects()
  const { data: suites, isLoading } = useTestSuites(
    selectedProjectId !== 'all'
      ? {
          project_id: parseInt(selectedProjectId, 10),
          limit: PAGE_SIZE,
          offset: (currentPage - 1) * PAGE_SIZE,
        }
      : {
          limit: PAGE_SIZE,
          offset: (currentPage - 1) * PAGE_SIZE,
        }
  )
  const deleteMutation = useDeleteSuite()

  // Client-side search filter
  const filteredSuites = (suites || []).filter((suite) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      suite.name.toLowerCase().includes(query) ||
      suite.details?.toLowerCase().includes(query)
    )
  })

  // Find project name for a suite's parent_id
  const getProjectName = (parentId: number): string => {
    const project = projects?.find((p) => p.id === parentId)
    return project
      ? project.prefix
        ? `[${project.prefix}] ${project.name}`
        : project.name
      : `Project #${parentId}`
  }

  // Handlers
  const handleCreate = () => {
    setDialogMode('create')
    setSelectedSuite(undefined)
    setDialogOpen(true)
  }

  const handleEdit = (suite: TestSuite) => {
    setDialogMode('edit')
    setSelectedSuite(suite)
    setDialogOpen(true)
  }

  const handleDeleteClick = (suite: TestSuite) => {
    setSuiteToDelete(suite)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!suiteToDelete) return

    try {
      await deleteMutation.mutateAsync(suiteToDelete.id)
      toast.success(`Suite "${suiteToDelete.name}" deleted`)
    } catch (err: unknown) {
      let msg = 'Failed to delete suite'
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { detail?: string } } }
        if (axiosErr.response?.data?.detail) {
          msg = axiosErr.response.data.detail
        }
      }
      toast.error(msg)
    } finally {
      setDeleteDialogOpen(false)
      setSuiteToDelete(null)
    }
  }

  const handleProjectFilter = (value: string) => {
    setSelectedProjectId(value)
    setCurrentPage(1)
  }

  // Determine total pages (suites array length + offset heuristic)
  const hasMore = filteredSuites.length === PAGE_SIZE

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Test Suites</h1>
          <p className="text-muted-foreground">
            Organize test cases into logical suites within projects.
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Create Suite
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search suites by name or details..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select
              value={selectedProjectId}
              onValueChange={handleProjectFilter}
            >
              <SelectTrigger className="w-full sm:w-[280px]">
                <SelectValue placeholder="Filter by project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects?.map((project) => (
                  <SelectItem key={project.id} value={project.id.toString()}>
                    {project.prefix ? `[${project.prefix}] ` : ''}
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Suites Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {isLoading
              ? 'Loading...'
              : filteredSuites.length > 0
                ? `${filteredSuites.length} suite${filteredSuites.length !== 1 ? 's' : ''}${searchQuery ? ' (filtered)' : ''}`
                : 'No test suites found'}
          </CardTitle>
          <CardDescription>
            {selectedProjectId !== 'all'
              ? `Showing suites for ${getProjectName(parseInt(selectedProjectId, 10))}`
              : 'Showing suites across all projects'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredSuites.length > 0 ? (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Suite Name</TableHead>
                      <TableHead>Parent Project</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead className="text-center">Test Cases</TableHead>
                      <TableHead className="text-center">Sub-Suites</TableHead>
                      <TableHead>Order</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSuites.map((suite) => (
                      <TableRow key={suite.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FolderTree className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{suite.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {getProjectName(suite.parent_id)}
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-muted-foreground">
                          {suite.details || '\u2014'}
                        </TableCell>
                        <TableCell className="text-center">
                          {suite.testcase_count != null ? (
                            <Badge variant="secondary">
                              {suite.testcase_count}
                            </Badge>
                          ) : (
                            '\u2014'
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {suite.subsuite_count != null ? (
                            <Badge variant="outline">
                              {suite.subsuite_count}
                            </Badge>
                          ) : (
                            '\u2014'
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {suite.node_order}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(suite.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(suite)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteClick(suite)}
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Page {currentPage}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={!hasMore}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <FolderTree className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-4 text-lg font-semibold">No test suites</h3>
              <p className="mt-2 text-muted-foreground">
                {searchQuery
                  ? 'No suites match your search criteria.'
                  : selectedProjectId !== 'all'
                    ? 'No suites exist for this project yet.'
                    : 'Get started by creating your first test suite.'}
              </p>
              {!searchQuery && (
                <Button onClick={handleCreate} className="mt-4">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Suite
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <SuiteDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        suite={selectedSuite}
        mode={dialogMode}
        defaultProjectId={
          selectedProjectId !== 'all'
            ? parseInt(selectedProjectId, 10)
            : undefined
        }
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Test Suite</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{suiteToDelete?.name}
              &rdquo;? This will cascade-delete all child suites and remove
              test case memberships. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Suite'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
